import styled, { keyframes } from 'styled-components';

import { Text } from '../../../../components/Text/Text';
import { ANIM, COLORS, MOBILE, REDUCED_MOTION, SPACE } from '../../../../theme/tokens';

export const Root = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${SPACE[16]};

  ${MOBILE} {
    flex-direction: column;
    align-items: stretch;
    gap: ${SPACE[8]};
  }
`;

export const NameBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
  min-width: 0;
  flex: 1 1 auto;
`;

export const Eyebrow = styled(Text)`
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${COLORS.textFaint};
`;

/* The name and its pencil, shrinking (with an ellipsis) as one unit so the pencil stays beside the name. */
export const NameRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[4]};
  max-width: 100%;
  min-width: 0;
`;

/* The name reads as a title; its field chrome only appears on hover / focus. It is as wide as its text. */
export const NameInput = styled.input`
  flex: 0 1 auto;
  min-width: 0;
  field-sizing: content;
  box-sizing: border-box;
  text-overflow: ellipsis;
  /* -8px pulls the text flush with the eyebrow while the (invisible) field keeps its inner padding. */
  margin-left: -${SPACE[8]};
  padding: ${SPACE[4]} ${SPACE[8]};
  border-radius: 10px;
  border: 1px solid transparent;
  background: transparent;
  color: ${COLORS.heading};
  font-size: 24px;
  font-weight: 600;
  line-height: 1.25;
  outline: none;
  transition:
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &::placeholder {
    color: ${COLORS.textFaint};
  }

  &:hover {
    border-color: ${COLORS.hairline};
  }

  &:focus {
    border-color: ${COLORS.ctaSolid};
    background: ${COLORS.surfaceFaint};
  }

  ${MOBILE} {
    font-size: 20px;
  }
`;

export const NameEdit = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${SPACE[4]};
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: ${COLORS.textFaint};
  cursor: pointer;
  transition: color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    color: ${COLORS.ctaSolid};
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.ctaSolid};
    outline-offset: 2px;
  }
`;

/* On a phone the two switches and the save line wrap onto as many rows as they need. */
export const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
  flex: 0 0 auto;

  ${MOBILE} {
    flex-wrap: wrap;
    gap: ${SPACE[8]} ${SPACE[12]};
  }
`;

export const SaveLine = styled.span<{ $visible: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[4]};
  min-height: 20px;
  font-size: 13px;
  color: ${COLORS.textMuted};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity ${ANIM.CREATIVE_VIEW_IN_MS}ms ${ANIM.EASE};

  /* On a phone the action sits flush left under the name: the save line (it keeps its room while hidden) follows it
     instead of pushing it in. */
  ${MOBILE} {
    order: 1;
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const SaveSpinner = styled.span`
  width: 12px;
  height: 12px;
  box-sizing: border-box;
  border-radius: 999px;
  border: 2px solid ${COLORS.hairline};
  border-top-color: ${COLORS.accent};
  animation: ${spin} ${ANIM.SPIN_MS}ms linear infinite;

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

export const RetryLink = styled.button`
  padding: 0;
  margin-left: ${SPACE[4]};
  border: none;
  background: none;
  color: ${COLORS.accent};
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
`;
